import React, { useContext, useState } from "react";
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Button,
  Heading,
  Text,
  useColorModeValue,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { Trans } from "@lingui/react/macro";
import AuthContext from "../../context/AuthContext";
import useAxios from "../../utils/useAxios";
import { useNavigate } from "react-router-dom";

const ChangePasswordCard: React.FC = () => {
  const toast = useToast();
  const api = useAxios();
  const { logoutUser } = useContext(AuthContext);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isValid =
    oldPassword.trim() !== "" && newPassword.trim() !== "" && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      setErrors("Please fill all fields and ensure the new passwords match.");
      return;
    }
    setLoading(true);
    setErrors(null);
    try {
      await api.post("users/password/change/", {
        old_password: oldPassword,
        new_password1: newPassword,
        new_password2: confirmPassword,
      });
      toast({
        title: "Password changed",
        description: "Your password has been updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      logoutUser();
    } catch (error: any) {
      const msg = error.response?.data?.detail || "Failed to change password.";
      setErrors(msg);
      toast({
        title: "Error",
        description: msg,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={useColorModeValue("gray.50", "gray.900")}
    >
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
        <Stack align="center">
          <Heading fontSize="4xl">
            <Trans>Change Password</Trans>
          </Heading>
        </Stack>
        <Box
          rounded="lg"
          as="form"
          bg={useColorModeValue("white", "gray.800")}
          boxShadow="lg"
          p={8}
          onSubmit={handleSubmit}
        >
          <Stack spacing={4}>
            <FormControl id="old-password" isRequired>
              <FormLabel>
                <Trans>Current Password</Trans>
              </FormLabel>
              <Input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
              />
            </FormControl>

            <FormControl id="new-password" isRequired>
              <FormLabel>
                <Trans>New Password</Trans>
              </FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormControl>

            <FormControl
              id="confirm-password"
              isRequired
              isInvalid={newPassword !== confirmPassword}
            >
              <FormLabel>
                <Trans>Confirm New Password</Trans>
              </FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              <FormErrorMessage>
                <Trans>Passwords must match.</Trans>
              </FormErrorMessage>
            </FormControl>

            {errors && <Text color="red.500">{errors}</Text>}

            <Stack spacing={10}>
              <Button
                bg="green.600"
                color="white"
                _hover={{ bg: "green.700" }}
                type="submit"
                isLoading={loading}
                isDisabled={!isValid || loading}
              >
                <Trans>Change Password</Trans>
              </Button>
              <Link color="green.400" onClick={() => navigate("/profile")}>
                <Trans>Back to Profile</Trans>
              </Link>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Flex>
  );
};

export default ChangePasswordCard;
